import React, { Suspense } from 'react';
import type { ComponentType } from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles, @atlaskit/ui-styling-standard/use-compiled -- The legacy fallback must inject the existing global extension-container styles until the dynamic kill switch is removed.
import { Global } from '@emotion/react';

import type { ExtensionComponentProps, Parameters } from '@atlaskit/editor-common/extensions';
import { NativeEmbedInitialPlaceholder } from '@atlaskit/native-embeds-common/initial-placeholder';
import type { NativeEmbedParameters } from '@atlaskit/native-embeds-common/types';
import { getParameters } from '@atlaskit/native-embeds-common/utils';
import { dynamicConfigBooleanValue } from '@atlaskit/tmp-editor-statsig/dynamic-config-boolean-value';
import { getNativeEmbedFacade } from '@atlassian/native-embeds-core/services/main';

import { nativeEmbedAlignmentStyles } from './styled';
import type NativeEmbedUIComponent from './ui';
import {
	NativeEmbedInitialPlaceholder as LegacyNativeEmbedInitialPlaceholder,
	resolveNativeEmbedInitialLayout,
} from './ui/initial-placeholder';

const NATIVE_EMBED_SSR_PLACEHOLDER_LAYOUT_KILLSWITCH =
	'native_embeds_ssr_placeholder_layout_killswitch';

type NativeEmbedExtensionParams = Parameters & NativeEmbedParameters;
export type NativeEmbedUILoaderProps = React.ComponentProps<typeof NativeEmbedUIComponent>;

const NativeEmbedUI = React.lazy<ComponentType<NativeEmbedUILoaderProps>>(() =>
	import(/* webpackChunkName: "@atlassian/native-embeds-editor-extension" */ './ui').then(
		({ default: NativeEmbedUIComponent }) => ({
			default: NativeEmbedUIComponent,
		}),
	),
);

export const NativeEmbedLoadingPlaceholder = ({
	node,
}: ExtensionComponentProps<NativeEmbedExtensionParams>): React.JSX.Element => {
	const { parameters } = node;
	const { url } = getParameters(parameters);
	const manifest = url ? getNativeEmbedFacade().getManifestByUrl(url) : undefined;

	if (!dynamicConfigBooleanValue(NATIVE_EMBED_SSR_PLACEHOLDER_LAYOUT_KILLSWITCH)) {
		return <NativeEmbedInitialPlaceholder manifest={manifest} parameters={parameters} />;
	}

	const initialLayout = resolveNativeEmbedInitialLayout({ manifest, parameters });

	return (
		<>
			<Global styles={nativeEmbedAlignmentStyles} />
			<LegacyNativeEmbedInitialPlaceholder {...initialLayout} />
		</>
	);
};

/**
 * Synchronous shell for the editor extension renderer.
 *
 * Editor's generic async extension loading state renders `null`, which causes the native embed block to
 * collapse before the real UI chunk arrives. This small shell keeps the full UI split into its existing
 * async chunk while providing a dimensionally accurate placeholder immediately.
 */
export default function NativeEmbedUILoader(props: NativeEmbedUILoaderProps): React.JSX.Element {
	return (
		<Suspense fallback={<NativeEmbedLoadingPlaceholder {...props} />}>
			<NativeEmbedUI {...props} />
		</Suspense>
	);
}
